import {Icon} from '@iconify/react';

import React, {useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {RankPositionFilter} from '../../../util/enums';

interface Props {
    positionCounts: Record<RankPositionFilter, number>;
    prevPositionCounts: Record<RankPositionFilter, number>;
    activeFilter: RankPositionFilter;
    onFilterChange: (filter: RankPositionFilter) => void;
}

interface FilterStat {
    label: RankPositionFilter;
    count: number;
    percent: number;
    delta: number;
}

const FILTER_LABELS: ReadonlyArray<RankPositionFilter> = [
    RankPositionFilter.ALL,
    RankPositionFilter.TOP_1,
    RankPositionFilter.TOP_3,
    RankPositionFilter.TOP_5,
    RankPositionFilter.TOP_10,
    RankPositionFilter.TOP_30,
    RankPositionFilter.BOTTOM_100
];

export default function PositionFilterBar({positionCounts, prevPositionCounts, activeFilter, onFilterChange}: Props) {
    const {t} = useTranslation();
    const stats = useMemo((): Array<FilterStat> => {
        const prevCounts = prevPositionCounts || {};
        const total = positionCounts[RankPositionFilter.ALL] || 1;

        return FILTER_LABELS.map(label => {
            const count = positionCounts[label] || 0;
            const prevCount = prevCounts[label] || 0;
            return {
                label,
                count,
                percent: Math.round((count / total) * 100),
                delta: count - prevCount
            };
        });
    }, [positionCounts, prevPositionCounts]);

    function renderPositionLabel(position: RankPositionFilter) {
        return {
            [RankPositionFilter.ALL]: t('searchRankings.positionFilter.all', 'Alle'),
            [RankPositionFilter.TOP_1]: t('searchRankings.positionFilter.top1', 'Top 1'),
            [RankPositionFilter.TOP_3]: t('searchRankings.positionFilter.top3', 'Top 3'),
            [RankPositionFilter.TOP_5]: t('searchRankings.positionFilter.top5', 'Top 5'),
            [RankPositionFilter.TOP_10]: t('searchRankings.positionFilter.top10', 'Top 10'),
            [RankPositionFilter.TOP_30]: t('searchRankings.positionFilter.top30', 'Top 30'),
            [RankPositionFilter.BOTTOM_100]: t('searchRankings.positionFilter.bottom100', '>100')
        }[position];
    }

    return (
        <div className='search-rankings-position-filters'>
            <div className='position-filter-label'>
                <span>{t('searchRankings.positionFilter.label', 'Positiefilters:')}</span>
            </div>

            {/* Percentages row */}
            <div className='position-filter-stats'>
                {stats.map(s => (
                    <div key={`pct-${s.label}`} className='position-stat'>
                        <span className='stat-percent'>
                            {s.label === RankPositionFilter.ALL ? '\u00A0' : `${s.percent}%`}
                        </span>
                    </div>
                ))}
            </div>

            {/* Filter buttons */}
            <div className='position-filter-bar'>
                {stats.map(s => (
                    <button
                        key={`btn-${s.label}`}
                        className={`position-filter-btn${activeFilter === s.label ? ' active' : ''}`}
                        onClick={() => onFilterChange(s.label)}
                    >
                        {renderPositionLabel(s.label)}
                    </button>
                ))}
            </div>

            {/* Counts row with deltas */}
            <div className='position-filter-stats'>
                {stats.map(s => (
                    <div key={`cnt-${s.label}`} className='position-stat'>
                        <span className='stat-count'>{s.count}</span>
                        <span
                            className={`stat-delta ${s.delta > 0 ? 'positive' : s.delta < 0 ? 'negative' : ''}`}
                            style={
                                s.label === RankPositionFilter.ALL || s.delta === 0 ? {visibility: 'hidden'} : undefined
                            }
                        >
                            <Icon
                                icon='mdi:triangle'
                                style={{
                                    fontSize: '0.5rem',
                                    transform: s.delta < 0 ? 'rotate(180deg)' : 'none'
                                }}
                            />
                            {Math.abs(s.delta) || 0}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}
